import { curLang, type Lang } from './i18n';

/**
 * Cum îi spune firma locului unde ajunge marfa. În construcții e șantierul; un
 * hotel are locații, o fermă are ferme, o fabrică puncte de lucru, o firmă de
 * servicii proiecte. În bază rămâne `Site` peste tot; doar cuvintele se schimbă.
 *
 * Textele sunt scrise o singură dată, cu „șantier” (substantiv neutru).
 * Pentru cuvintele feminine („locație”, „fermă”) se schimbă și acordul din jurul
 * lor: „un șantier nou” → „o locație nouă” (vezi `siteText`).
 */

export type SiteTermId = 'santier' | 'proiect' | 'punct' | 'locatie' | 'ferma';

export interface SiteTerm {
  id: SiteTermId;
  /** cum apare în alegerea din „Contul firmei” */
  label: string;
  /** genul: 'n' (șantier, proiect, punct de lucru) sau 'f' (locație, fermă) */
  gender: 'n' | 'f';
  /** șantier */
  one: string;
  /** șantierul */
  the: string;
  /** șantierului */
  of: string;
  /** (fiecărui) șantier: genitivul fără articol */
  ofOne: string;
  /** șantiere */
  many: string;
  /** șantierele */
  theMany: string;
  /** șantierelor */
  ofMany: string;
  /** unde trebuie marfa: „pe șantier” */
  on: string;
  /** de unde vine necesarul: „de pe șantier” */
  fromOn: string;
  /** prepoziția din „pe șantierul X”: „pe” sau „la” */
  at: string;
  /** „șef de șantier”: [nehotărât, hotărât, genitiv, plural, plural genitiv] */
  boss: readonly [string, string, string, string, string];
  /** în engleză: [site, sites, site manager, site managers] */
  en: readonly [string, string, string, string];
}

export const SITE_TERMS: Record<SiteTermId, SiteTerm> = {
  santier: {
    id: 'santier',
    label: 'Șantiere',
    gender: 'n',
    one: 'șantier',
    the: 'șantierul',
    of: 'șantierului',
    ofOne: 'șantier',
    many: 'șantiere',
    theMany: 'șantierele',
    ofMany: 'șantierelor',
    on: 'pe șantier',
    fromOn: 'de pe șantier',
    at: 'pe',
    boss: [
      'șef de șantier',
      'șeful de șantier',
      'șefului de șantier',
      'șefii de șantier',
      'șefilor de șantier',
    ],
    en: ['site', 'sites', 'site manager', 'site managers'],
  },
  locatie: {
    id: 'locatie',
    label: 'Locații',
    gender: 'f',
    one: 'locație',
    the: 'locația',
    of: 'locației',
    ofOne: 'locații',
    many: 'locații',
    theMany: 'locațiile',
    ofMany: 'locațiilor',
    on: 'la locație',
    fromOn: 'de la locație',
    at: 'la',
    boss: [
      'responsabil de locație',
      'responsabilul locației',
      'responsabilului locației',
      'responsabilii locațiilor',
      'responsabililor locațiilor',
    ],
    en: ['location', 'locations', 'location manager', 'location managers'],
  },
  punct: {
    id: 'punct',
    label: 'Puncte de lucru',
    gender: 'n',
    one: 'punct de lucru',
    the: 'punctul de lucru',
    of: 'punctului de lucru',
    ofOne: 'punct de lucru',
    many: 'puncte de lucru',
    theMany: 'punctele de lucru',
    ofMany: 'punctelor de lucru',
    on: 'la punctul de lucru',
    fromOn: 'de la punctul de lucru',
    at: 'la',
    boss: [
      'șef de punct de lucru',
      'șeful punctului de lucru',
      'șefului punctului de lucru',
      'șefii punctelor de lucru',
      'șefilor punctelor de lucru',
    ],
    en: ['site', 'sites', 'site manager', 'site managers'],
  },
  ferma: {
    id: 'ferma',
    label: 'Ferme',
    gender: 'f',
    one: 'fermă',
    the: 'ferma',
    of: 'fermei',
    ofOne: 'ferme',
    many: 'ferme',
    theMany: 'fermele',
    ofMany: 'fermelor',
    on: 'la fermă',
    fromOn: 'de la fermă',
    at: 'la',
    boss: ['șef de fermă', 'șeful fermei', 'șefului fermei', 'șefii fermelor', 'șefilor fermelor'],
    en: ['farm', 'farms', 'farm manager', 'farm managers'],
  },
  proiect: {
    id: 'proiect',
    label: 'Proiecte',
    gender: 'n',
    one: 'proiect',
    the: 'proiectul',
    of: 'proiectului',
    ofOne: 'proiect',
    many: 'proiecte',
    theMany: 'proiectele',
    ofMany: 'proiectelor',
    on: 'la proiect',
    fromOn: 'de la proiect',
    at: 'la',
    boss: [
      'șef de proiect',
      'șeful de proiect',
      'șefului de proiect',
      'șefii de proiect',
      'șefilor de proiect',
    ],
    en: ['project', 'projects', 'project manager', 'project managers'],
  },
};

export const SITE_TERM_IDS = Object.keys(SITE_TERMS) as SiteTermId[];

export const isSiteTerm = (v: string): v is SiteTermId => v in SITE_TERMS;

/** Firmele de dinainte și orice valoare necunoscută rămân pe „șantier”. */
export const siteTermOf = (id: string | null | undefined): SiteTerm =>
  SITE_TERMS[id && isSiteTerm(id) ? id : 'santier'];

/** Prima literă mare: „Șantierul”, „Punctele de lucru”. */
export const cap = (s: string): string => s.charAt(0).toLocaleUpperCase('ro') + s.slice(1);

/** „1 șantier”, „3 șantiere”, „20 de șantiere” (ca în română: de la 20 în sus, cu „de”). */
export function countSites(n: number, t: SiteTerm): string {
  if (n === 1) return `1 ${t.one}`;
  const r = n % 100;
  return `${n} ${n !== 0 && (r === 0 || r >= 20) ? 'de ' : ''}${t.many}`;
}

const L = '\\p{L}';
const word = (w: string) => new RegExp(`(?<!${L})${w}(?!${L})`, 'giu');

/** Păstrează litera mare de la început: „Șantierul” → „Proiectul”. */
const sameCase = (found: string, to: string) =>
  found.charAt(0) !== found.charAt(0).toLocaleLowerCase('ro') ? cap(to) : to;

/** Cuvântul de dinaintea substantivului, la feminin: „un șantier” → „o locație”. */
const BEFORE_F: Record<string, string> = {
  un: 'o',
  niciun: 'nicio',
  acest: 'această',
  alt: 'altă',
  primul: 'prima',
  'un singur': 'o singură',
};
/** La genitiv, cu forma fără articol: „fiecărui șantier” → „fiecărei locații”. */
const BEFORE_F_OF: Record<string, string> = { fiecărui: 'fiecărei', unui: 'unei', acestui: 'acestei' };

/** Adjectivele care se acordă cu substantivul, la feminin. */
const ADJ_F: Record<string, string> = {
  nou: 'nouă',
  deschis: 'deschisă',
  închis: 'închisă',
  redeschis: 'redeschisă',
  șters: 'ștearsă',
  adăugat: 'adăugată',
  actualizat: 'actualizată',
  ales: 'aleasă',
  găsit: 'găsită',
  activ: 'activă',
  terminat: 'terminată',
};

/** Pronumele care țin locul substantivului, la feminin; doar în textele care îl pomenesc. */
const PRONOUN_F: [string, string][] = [
  ['Închide-l', 'Închide-o'],
  ['Redeschide-l', 'Redeschide-o'],
  ['să-l adauge', 's-o adauge'],
  ['Adaugă unul', 'Adaugă una'],
  ['încă unul', 'încă una'],
  ['Adaugă primul', 'Adaugă prima'],
  ['alege altul', 'alege alta'],
  ['poate fi șters', 'poate fi ștearsă'],
];

/** În afara construcțiilor se cumpără produse, nu materiale. Toate neutre. */
const GOODS: [string, string][] = [
  ['materialelor', 'produselor'],
  ['materialele', 'produsele'],
  ['materialului', 'produsului'],
  ['materialul', 'produsul'],
  ['materiale', 'produse'],
  ['material', 'produs'],
];

/**
 * Textele sunt scrise o singură dată, cu „șantier”; aici se schimbă cuvântul
 * pentru firmele care spun altfel: formele substantivului, „pe” → „la”, „șef de
 * șantier”, iar la cuvintele feminine și acordul (articolul nehotărât,
 * adjectivele de după, pronumele). „Materiale” devine „produse” și
 * „antemăsurătoare”, „listă de cantități”. Pentru șantier, textul rămâne neatins.
 */
export function siteText(text: string, t: SiteTerm, lang: Lang = curLang()): string {
  if (lang === 'en') return siteTextEn(text, t);
  if (t.id === 'santier' || !text) return text;
  let out = text;
  const SING = '(?:șantierului|șantierul|șantier)';
  if (t.gender === 'f') {
    const mentions = new RegExp(`(?<!${L})${SING}(?!${L})`, 'iu').test(out);
    // adjectivul de după: „Șantier nou”, „Șantierul „X” e închis”, „nu a fost găsit”
    out = out.replace(
      new RegExp(
        `((?<!${L})${SING}(?: „[^”]*”)?(?: (?:e|este|a fost|nu a fost|nu e|nu poate fi))? )(${Object.keys(ADJ_F).join('|')})(?!${L})`,
        'giu',
      ),
      (_m, lead: string, adj: string) => lead + sameCase(adj, ADJ_F[adj.toLocaleLowerCase('ro')]!),
    );
    out = out.replace(
      new RegExp(`(?<!${L})(${Object.keys(BEFORE_F_OF).join('|')}) șantier(?!${L})`, 'giu'),
      (_m, w: string) => `${sameCase(w, BEFORE_F_OF[w.toLocaleLowerCase('ro')]!)} ${t.ofOne}`,
    );
    out = out.replace(
      new RegExp(`(?<!${L})(un singur|niciun|acest|primul|alt|un) (?=șantier(?!${L}))`, 'giu'),
      (_m, w: string) => `${sameCase(w, BEFORE_F[w.toLocaleLowerCase('ro')]!)} `,
    );
    if (mentions) for (const [from, to] of PRONOUN_F) out = out.split(from).join(to);
  }
  // „pe șantier” e fără articol; „la punctul de lucru” îl cere, deci vine întreg din termen
  out = out.replace(new RegExp(`(?<!${L})(de )?pe șantier(?!${L})`, 'giu'), (m, de: string | undefined) =>
    sameCase(m, de ? t.fromOn : t.on),
  );
  // „de pe șantierul X”, „pe acest șantier”: marfa ajunge „la” un proiect sau o locație
  out = out.replace(
    new RegExp(
      `(?<!${L})(de )?pe (?=(?:(?:acest|această|un|o|niciun|nicio|fiecare|alt|altă) )?șantier)`,
      'giu',
    ),
    (m, de: string | undefined) => sameCase(m, `${de ? 'de ' : ''}${t.at} `),
  );
  const forms: [string, string][] = [
    ['șefilor de șantier', t.boss[4]],
    ['șefii de șantier', t.boss[3]],
    ['șefului de șantier', t.boss[2]],
    ['șeful de șantier', t.boss[1]],
    ['șef de șantier', t.boss[0]],
    ['șantierelor', t.ofMany],
    ['șantierele', t.theMany],
    ['șantierului', t.of],
    ['șantierul', t.the],
    ['șantiere', t.many],
    ['șantier', t.one],
    ['antemăsurătorilor', 'listelor de cantități'],
    ['antemăsurătoarea', 'lista de cantități'],
    ['antemăsurătorii', 'listei de cantități'],
    ['antemăsurătoare', 'listă de cantități'],
    ['antemăsurători', 'liste de cantități'],
    ...GOODS,
  ];
  for (const [from, to] of forms) out = out.replace(word(from), (m) => sameCase(m, to));
  return out;
}

/** În engleză, „materials” → „products” în afara construcțiilor, ca în română. */
const GOODS_EN: [string, string][] = [
  ['bills of quantities', 'quantity lists'],
  ['bill of quantities', 'quantity list'],
  ['materials', 'products'],
  ['material', 'product'],
];

const wordEn = (w: string) => new RegExp(`(?<![\\p{L}-])${w}(?![\\p{L}-])`, 'giu');
const sameCaseEn = (found: string, to: string) =>
  found.charAt(0) !== found.charAt(0).toLowerCase() ? to.charAt(0).toUpperCase() + to.slice(1) : to;

/**
 * Textele în engleză sunt scrise cu „site”; aici devin „location”, „farm” sau
 * „project”. În engleză nu e acord, deci ajung substantivul și „site manager”.
 * Un proiect se lucrează „on”, nu „at”: „at the site” → „on the project”.
 */
export function siteTextEn(text: string, t: SiteTerm): string {
  if (!text) return text;
  let out = text;
  const [one, many, boss, bosses] = t.en;
  if (one !== 'site') {
    if (t.id === 'proiect') {
      out = out.replace(
        /\b(at|At) (the|this|each|a|every|that|one) site\b/g,
        (_m, at: string, w: string) => `${at === 'At' ? 'On' : 'on'} ${w} site`,
      );
    }
    // „on site” e doar pentru șantier: „at the location”, „at the farm”, „for the project”
    out = out.replace(/\b(on|On) site\b/g, (_m, on: string) => {
      const prep = t.id === 'proiect' ? 'for' : 'at';
      return `${on === 'On' ? prep.charAt(0).toUpperCase() + prep.slice(1) : prep} the site`;
    });
    const forms: [string, string][] = [
      ['site managers', bosses],
      ['site manager', boss],
      ['sites', many],
      ['site', one],
    ];
    for (const [from, to] of forms) out = out.replace(wordEn(from), (m) => sameCaseEn(m, to));
  }
  if (t.id !== 'santier')
    for (const [from, to] of GOODS_EN) out = out.replace(wordEn(from), (m) => sameCaseEn(m, to));
  return out;
}
